"use client";

import { useEffect, useState } from "react";
import type { TocItem } from "@/lib/blog/toc";

/** Indice sticky: evidenzia la sezione attiva via IntersectionObserver. */
export function ArticleToc({ items, label }: { items: TocItem[]; label: string }) {
  const [activeId, setActiveId] = useState<string>(items[0]?.id ?? "");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const headings = items
      .map((i) => document.getElementById(i.id))
      .filter((el): el is HTMLElement => el != null);
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target.id) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-96px 0px -70% 0px", threshold: 0 }
    );
    headings.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  return (
    <nav aria-label={label} className="text-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="mb-3 flex w-full items-center justify-between font-mono text-[11px] uppercase tracking-[0.22em] text-primary/90 lg:cursor-default lg:pointer-events-none"
      >
        {label}
        <span className={`lg:hidden transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true">
          v
        </span>
      </button>
      <ul className={`${open ? "block" : "hidden"} space-y-1.5 lg:block`}>
        {items.map((item) => (
          <li key={item.id} className={item.depth === 3 ? "pl-3" : ""}>
            <a
              href={`#${item.id}`}
              onClick={() => setOpen(false)}
              className={`block border-l-2 py-1 pl-3 transition-colors ${
                activeId === item.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {item.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
